import { getTranslatedItems, updateLocaleAsync } from '@/plugins/i18n'
import { ref, toRaw } from 'vue'
import { defineStore } from 'pinia'

import * as LocalizationApi from '@/apis/localizations.api'
import { useWebSiteStore } from '@/stores/website.store'

export const initLocalizationStoreAsync = async(userDefaultLanguage = null) => {
  const store = useLocalizationStore()
  await store.fetchLanguagesAsync()

  const websiteStore = useWebSiteStore()

  const languageToUse =
   store.selectedLanguage ||
   userDefaultLanguage ||
   window.config.DEFAULT_LANGUAGE

  const isVersionOutdated = await websiteStore.updateVersionAsync()
  await store.setLanguageAsync(languageToUse, isVersionOutdated)
}

export const useLocalizationStore = defineStore('localization', () => {
  const languages = ref([])
  const localizations = ref({})
  const selectedLanguage = ref(null)

  const $reset = () => {
    languages.value = []
    localizations.value = {}
  }

  const fetchLanguagesAsync = async() => {
    const res = await LocalizationApi.fetchLanguagesAsync()
    if (res.data) {
      languages.value = res.data
    }
  }

  const fetchLocalizationsAsync = async(languageCode, forceUpdate = false) => {
    if ((!localizations.value[languageCode]) || localizations.value[languageCode].length === 0 || forceUpdate) {
      const res = await LocalizationApi.fetchLocalizationsAsync(languageCode)
      if (res.data) {
        localizations.value[languageCode] = res.data
      }
    }
  }

  const getLocalizedLanguages = () => getTranslatedItems(languages.value)

  const localizationsReset = () => {
    localizations.value = {}
  }

  const setLanguageAsync = async(languageCode = navigator.languageCode || window.config.DEFAULT_LANGUAGE, forceUpdate = false) => {
    if (languages.value.length === 0 || !languages.value.some(l => l.code === languageCode)) {
      languageCode = window.config.DEFAULT_LANGUAGE
    }

    await fetchLocalizationsAsync(languageCode, forceUpdate)
    await updateLocaleAsync(languageCode, toRaw(localizations.value))
    selectedLanguage.value = languageCode
  }

  return {
    languages,
    localizations,
    selectedLanguage,

    $reset,

    fetchLanguagesAsync,
    fetchLocalizationsAsync,
    getLocalizedLanguages,
    localizationsReset,
    setLanguageAsync,
  }
}, {
  persist: [
    {
      pick: ['languages', 'localizations', 'selectedLanguage'],
      storage: localStorage
    }
  ]
})
